import {Box, MantineNumberSize} from '@mantine/core';
import React from 'react';

import useMoleculeSchemasPairing from 'chemistry/bulk-upload/useMoleculeSchemasPairing';
import {LABELING_STRATEGY_TO_TEXT} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';

/**
 * Whether the molecules being created have salts or solvents. Salted molecules are created on the Form
 * schema and may auto-create a parent on the Compound schema; unsalted molecules are created on the
 * Compound schema.
 *
 * Use `mixed` when the upload can include both salted and unsalted molecules, such as bulk import,
 * so both registration paths are checked.
 */
export type MoleculeStructureKind = 'salted' | 'unsalted' | 'mixed';

/**
 * Returns the naming strategy an auto-created parent Compound is registered with.
 * Auto-created Compounds have synthetic names, so name-derived registry IDs are replaced with new IDs.
 */
function getParentNamingStrategy(namingStrategy: RegistryLabelingStrategy): RegistryLabelingStrategy {
  return namingStrategy === RegistryLabelingStrategy.IDS_FROM_NAMES
    ? RegistryLabelingStrategy.NEW_IDS
    : namingStrategy;
}

/**
 * Lists the naming strategies that molecules registered with the selected naming strategy also use on the
 * schema linked to the selected one, given the kind of structures being created.
 */
function getLinkedSchemaStrategies(
  isFormSelected: boolean,
  namingStrategy: RegistryLabelingStrategy,
  structureKind: MoleculeStructureKind
): Array<RegistryLabelingStrategy> {
  const hasSalted = structureKind !== 'unsalted';
  const hasUnsalted = structureKind !== 'salted';

  if (!isFormSelected) {
    return hasSalted ? [namingStrategy] : [];
  }
  const strategies: Array<RegistryLabelingStrategy> = [];
  if (hasSalted) {
    strategies.push(getParentNamingStrategy(namingStrategy));
  }
  if (hasUnsalted) {
    strategies.push(namingStrategy);
  }
  return strategies;
}

function getFailureMessage(linkedSchemaName: string, namingStrategy: RegistryLabelingStrategy): JSX.Element {
  const friendlyStrategy = LABELING_STRATEGY_TO_TEXT[namingStrategy] ?? namingStrategy;
  const schemaName = <strong>{linkedSchemaName}</strong>;
  return (
    <>
      Registering here can also register entities on the linked schema {schemaName}, which doesn&apos;t allow
      &quot;{friendlyStrategy}&quot;. Any entity registered on {schemaName} with &quot;{friendlyStrategy}
      &quot; will fail. Choose a naming strategy that {schemaName} also allows, or add &quot;
      {friendlyStrategy}&quot; to {schemaName}.
    </>
  );
}

interface LinkedSchemaNamingStrategyWarningProps {
  /** The schema the user selected, or null when none is selected. */
  selectedSchemaId: string | null;
  namingStrategy: RegistryLabelingStrategy | null;
  structureKind: MoleculeStructureKind;
  /** Bottom margin applied to the banner when it is rendered. */
  mb?: MantineNumberSize;
}

/**
 * Warning shown when molecules are registered with a naming strategy that the linked Form or Compound
 * schema does not enable. Creating a molecule can also create a molecule on the linked schema, and that
 * creation fails if the strategy is not enabled there.
 */
export default function LinkedSchemaNamingStrategyWarning({
  selectedSchemaId,
  namingStrategy,
  structureKind,
  mb,
}: LinkedSchemaNamingStrategyWarningProps): JSX.Element | null {
  const {pairingMap, schemasById} = useMoleculeSchemasPairing(null);
  const pairing = selectedSchemaId != null ? pairingMap[selectedSchemaId] : undefined;

  if (pairing == null || namingStrategy == null) {
    return null;
  }

  const isFormSelected = pairing.formSchemaId === selectedSchemaId;
  const linkedSchema = schemasById[isFormSelected ? pairing.compoundSchemaId : pairing.formSchemaId];
  if (linkedSchema == null) {
    return null;
  }

  const unsupportedStrategies = Array.from(
    new Set(getLinkedSchemaStrategies(isFormSelected, namingStrategy, structureKind))
  ).filter((strategy) => !linkedSchema.labelingStrategies.includes(strategy));
  if (unsupportedStrategies.length === 0) {
    return null;
  }

  return (
    <Box mb={mb}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text="Some molecules can't be registered with this naming strategy"
        supportingInfo={
          <Box>
            {unsupportedStrategies.map((strategy) => (
              <div key={strategy}>{getFailureMessage(linkedSchema.name, strategy)}</div>
            ))}
          </Box>
        }
        action={{
          actionType: InlineBannerActionType.ACCORDION,
          rightAlign: true,
          accordionLabels: {moreLabel: 'See more', lessLabel: 'See less'},
          initialShowMore: true,
        }}
      />
    </Box>
  );
}
